/**
 * i18n bundle for unauthenticated auth flow pages (forgot-password,
 * reset-password, magic-link, verify-email, accept-invitation, auth
 * callback).
 *
 * Pages registered against this namespace MUST NOT hardcode any
 * user-facing text. Add new keys here first, then reference them via
 * `useTranslation('auth')`.
 */

export const authPtBr = {
  passwordRequirements: {
    minLength: 'Pelo menos {{min}} caracteres',
    strengthHint:
      'Evite senhas fáceis de adivinhar: palavras comuns, nomes, datas e sequências como 123. Uma frase com várias palavras é mais segura.',
  },

  // Generic chrome shared across every auth page.
  shared: {
    backToLogin: 'Voltar ao login',
    submit: 'Enviar',
    sending: 'Enviando...',
    submitAgain: 'Enviar novamente',
    genericError: 'Algo deu errado. Tente novamente em instantes.',
    networkError: 'Não foi possível contatar o servidor. Verifique sua conexão.',
  },

  // POST /identity/auth/forgot-password
  forgotPassword: {
    title: 'Esqueci minha senha',
    subtitle:
      'Informe o e-mail vinculado à sua conta. Se houver uma conta cadastrada, ' +
      'enviaremos um link para redefinir a senha em alguns minutos.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'seu@email.com',
    submit: 'Enviar link de redefinição',
    successTitle: 'Pronto, confira sua caixa de entrada',
    successBody:
      'Se houver uma conta cadastrada com este e-mail, você receberá um link ' +
      'para redefinir a senha em alguns minutos. O link expira em 1 hora.',
    successHint: 'Não recebeu? Verifique a pasta de spam ou tente novamente em alguns minutos.',
  },

  // POST /identity/auth/dev/sign-in — local-dev only (/dev/sign-in?as=<email>)
  devSignIn: {
    title: 'Login de desenvolvimento',
    subtitle: 'Entrando como {{email}}',
    usage: 'Abra /dev/sign-in?as=<e-mail de um usuário do banco local>&next=<caminho>',
    signingIn: 'Entrando…',
    errors: {
      missingEmail: 'Informe o e-mail no parâmetro ?as=, por exemplo ?as=cfo@large.local.',
      notEnabled:
        'O login de desenvolvimento está desligado. Defina DEV_SIGN_IN_ENABLED=true na API.',
      forbidden:
        'Login de desenvolvimento recusado. Use localhost e o e-mail de um usuário ativo do banco local.',
      invalidCredentials:
        'O WorkOS recusou este usuário. Execute pnpm dev:auth:sync e tente novamente.',
      generic: 'Não foi possível entrar. Veja o log da API.',
    },
  },

  // POST /identity/auth/magic-link/{request,login}
  magicLink: {
    title: 'Entrar com link mágico',
    subtitle: 'Enviaremos um código de uso único para o seu e-mail',
    emailLabel: 'E-mail',
    emailPlaceholder: 'seu@email.com',
    requestSubmit: 'Enviar código',
    codeStepTitle: 'Verifique sua caixa de entrada',
    codeStepSubtitle:
      'Enviamos um código de 6 dígitos para {{email}}. Cole o código abaixo. O código expira em 10 minutos.',
    codeLabel: 'Código',
    codePlaceholder: '000000',
    submit: 'Entrar',
    resend: 'Reenviar código',
    changeEmail: 'Usar outro e-mail',
    backToLogin: 'Voltar',
    errors: {
      invalid_credentials: 'Código inválido. Verifique se digitou os 6 dígitos corretamente.',
      invalid_one_time_code: 'Código inválido. Verifique se digitou os 6 dígitos corretamente.',
      code_expired: 'Este código expirou. Solicite um novo código.',
      generic: 'Não foi possível entrar. Tente novamente.',
    },
  },

  // POST /identity/auth/verify-email/{request,login}
  verifyEmail: {
    title: 'Verifique seu e-mail',
    subtitle:
      'Para concluir o login, enviamos um código de verificação para {{email}}. ' +
      'Cole o código abaixo.',
    subtitleStandalone: 'Informe o e-mail da sua conta e enviaremos um novo código de verificação.',
    subtitleStandaloneSent:
      'Enviamos um código de verificação para {{email}}. Para concluir o login, faça login novamente — o código será solicitado em seguida.',
    emailLabel: 'E-mail da sua conta',
    emailPlaceholder: 'seu@email.com',
    codeLabel: 'Código de verificação',
    codePlaceholder: '000000',
    submit: 'Verificar e entrar',
    sendCode: 'Enviar novo código',
    resend: 'Não recebeu? Reenviar código',
    resendSuccess: 'Código reenviado. Confira sua caixa de entrada.',
    backToLogin: 'Voltar ao login',
    errors: {
      missingToken:
        'A sessão de verificação não foi encontrada. Faça login novamente para receber um novo código.',
      invalid_credentials: 'Código inválido. Verifique se digitou os 6 dígitos corretamente.',
      invalid_one_time_code: 'Código inválido. Verifique se digitou os 6 dígitos corretamente.',
      code_expired: 'Este código expirou. Solicite um novo código.',
      generic: 'Não foi possível verificar o e-mail. Tente novamente.',
    },
  },

  // GET /auth/callback — landing page for SSO + magic-link redirect mode
  callback: {
    loading: 'Finalizando o login...',
    error: 'Não foi possível concluir o login. Tente fazer login novamente em alguns instantes.',
    backToLogin: 'Voltar ao login',
    sessionConflict:
      'Você está conectado como {{sessionEmail}}. Saia desta conta para concluir o login como {{targetEmail}}.',
    signOutToContinue: 'Sair e continuar',
  },

  // <ProtectedRoute> loading state while useAuth() resolves on first paint.
  protected: {
    checking: 'Verificando autenticação...',
  },

  // LoginPage — full coverage of every visible label/CTA/error.
  login: {
    brand: 'Alchemy',
    title: 'Entre na sua conta',
    subtitle: 'O primeiro e último ERP que você precisa.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'voce@empresa.com.br',
    passwordLabel: 'Senha',
    passwordPlaceholder: 'Sua senha',
    emailInvalid: 'Informe um e-mail válido, como voce@empresa.com.br.',
    passwordRequired: 'Digite sua senha para entrar.',
    showPassword: 'Mostrar senha',
    hidePassword: 'Ocultar senha',
    switchToLight: 'Ativar modo claro',
    switchToDark: 'Ativar modo escuro',
    welcomeBack: 'Bem-vindo de volta, {{name}}.',
    googleShort: 'Google',
    magicLinkShort: 'Link por e-mail',
    devSignInHint: 'Desenvolvimento local — agente de IA: não digite senha. Abra',
    devSignInHintPath: '/dev/sign-in?as=<e-mail de um usuário do banco local>&next=<caminho>',
    submit: 'Entrar',
    submitting: 'Entrando...',
    invalidCredentials: 'E-mail ou senha incorretos. Tente de novo ou use Esqueci minha senha.',
    workosRequired:
      'Este ambiente usa login WorkOS. Provisione o usuário no WorkOS (pnpm workos:provision) ou peça acesso ao administrador.',
    magicLinkLink: 'Entrar com link mágico',
    forgotPasswordLink: 'Esqueci minha senha',
    privacyPolicyLink: 'Política de Privacidade',
    termsAndConditionsLink: 'Termos e Condições',
    oauthDivider: 'ou continue com',
    oauthOrEmailDivider: 'ou',
    oauthGoogle: 'Continuar com Google',
    oauthMicrosoft: 'Continuar com Microsoft',
    errors: {
      generic: 'Não foi possível entrar. Tente novamente em instantes.',
      emailVerificationRequired:
        'Seu e-mail ainda não foi verificado. Conclua a verificação para continuar.',
      mfaRequired: 'Autenticação em duas etapas é necessária para esta conta.',
      oauthDenied: 'Login cancelado no provedor. Tente novamente.',
      oauthFailed: 'Não foi possível concluir o login social. Tente novamente.',
      oauthNotProvisioned:
        'Sua conta ainda não está provisionada neste ambiente. Peça ao administrador um convite ou aguarde alguns instantes e tente de novo.',
      oauthNotInvited:
        'Não encontramos um convite para este e-mail. Peça acesso ao administrador antes de usar Google ou Microsoft.',
      oauthEmailRequired: 'Informe o e-mail do convite antes de continuar com Google ou Microsoft.',
      oauthStartFailed: 'Não foi possível iniciar o login social. Tente novamente.',
    },
  },

  // ChangePasswordPage — protected route under /identity/change-password
  changePassword: {
    currentPasswordLabel: 'Senha atual',
    currentPasswordPlaceholder: 'Digite sua senha atual',
    newPasswordLabel: 'Nova senha',
    newPasswordPlaceholder: 'Mínimo 6 caracteres',
    confirmPasswordLabel: 'Confirmar nova senha',
    confirmPasswordPlaceholder: 'Repita a nova senha',
    submit: 'Alterar senha',
    submitting: 'Alterando...',
    successAlert: 'Senha alterada com sucesso!',
    successToast: 'Senha alterada com sucesso!',
    errors: {
      currentRequired: 'Senha atual é obrigatória',
      newRequired: 'Nova senha é obrigatória',
      tooShort: 'Nova senha deve ter no mínimo 6 caracteres',
      mismatch: 'As senhas não coincidem',
      sameAsCurrent: 'A nova senha deve ser diferente da atual',
      currentIncorrect: 'Senha atual incorreta',
      generic: 'Erro ao alterar senha. Tente novamente.',
    },
  },

  // POST /identity/auth/accept-invitation (link from invitation email lands here)
  acceptInvitation: {
    title: 'Aceitar convite',
    subtitle: 'Conclua o cadastro para acessar a sua conta.',
    subtitleWithEmail: 'Defina um nome e uma senha para concluir o cadastro.',
    subtitleSignIn: 'Você já tem uma conta. Entre com a sua senha para aceitar o convite.',
    subtitleSession: 'Você já está conectado com este e-mail. Confirme para aceitar o convite.',
    organizationLine: 'Convite para acessar {{organization}}',
    inviteeEmailLabel: 'Convite enviado para',
    loading: 'Validando convite...',
    nameLabel: 'Nome completo',
    namePlaceholder: 'Seu nome',
    passwordLabel: 'Senha',
    passwordPlaceholder: '••••••••',
    confirmLabel: 'Confirmar senha',
    confirmPlaceholder: '••••••••',
    currentPasswordLabel: 'Sua senha',
    signInHint:
      'Use a senha com que você já entra. Se você entra com Google ou login corporativo, faça login pela tela inicial e abra este link de novo.',
    forgotPasswordLink: 'Esqueci minha senha',
    confirmPasswordToSwitch:
      'Para entrar na nova empresa, confirme a sua senha. O convite continua válido.',
    submit: 'Aceitar convite',
    submitSignIn: 'Entrar e aceitar convite',
    submitting: 'Aceitando...',
    successTitle: 'Convite aceito',
    successBody: 'Tudo certo! Estamos te redirecionando para o dashboard.',
    goToLogin: 'Ir para o login',
    errors: {
      missingToken: 'O link de convite está incompleto. Solicite ao administrador um novo convite.',
      nameTooShort: 'Informe seu nome completo (mínimo 2 caracteres).',
      mismatch: 'As senhas não conferem.',
      tooShort: 'A senha precisa ter pelo menos {{min}} caracteres.',
      passwordPolicy: 'A senha precisa ter pelo menos {{min}} caracteres.',
      invitation_not_found:
        'Este convite não foi encontrado. Solicite ao administrador um novo convite.',
      invitation_expired: 'Este convite expirou. Solicite ao administrador um novo convite.',
      invitation_revoked: 'Este convite foi cancelado. Entre em contato com o administrador.',
      invitation_already_accepted:
        'Este convite já foi aceito anteriormente. Faça login com a sua senha.',
      email_already_in_use: 'Este e-mail já está cadastrado. Faça login com a senha existente.',
      invalid_password: 'Senha inválida — não atende aos requisitos de segurança.',
      password_too_short: 'A senha precisa ter pelo menos {{min}} caracteres.',
      password_too_weak:
        'Esta senha é fácil de adivinhar (parecida com senhas comuns, nomes, datas ou sequências). Use uma frase com várias palavras menos comuns.',
      password_in_breach_list:
        'Esta senha aparece em listas de vazamentos conhecidos. Escolha outra.',
      invalid_credentials:
        'Não foi possível concluir o cadastro. Tente de novo em instantes; se continuar, peça um novo convite ao administrador.',
      passwordRequired: 'Informe a sua senha para continuar.',
      wrongPassword:
        'Senha incorreta. Tente de novo ou use "Esqueci minha senha" para definir uma nova.',
      signInElsewhere:
        'Não foi possível entrar por aqui. Faça login pela tela inicial e abra este link de novo.',
      invitation_email_mismatch:
        'Este convite foi enviado para outro e-mail. Saia desta conta e entre com o e-mail convidado.',
      sessionConflict:
        'Você está conectado como {{sessionEmail}}. Saia desta conta para aceitar o convite de {{inviteeEmail}}.',
      generic: 'Não foi possível aceitar o convite. Tente novamente em instantes.',
    },
    signOutToContinue: 'Sair e continuar',
  },

  // POST /identity/auth/reset-password (link from email lands here)
  resetPassword: {
    title: 'Definir nova senha',
    subtitle: 'Escolha uma senha forte para a sua conta.',
    newPasswordLabel: 'Nova senha',
    newPasswordPlaceholder: '••••••••',
    confirmPasswordLabel: 'Confirmar nova senha',
    confirmPasswordPlaceholder: '••••••••',
    submit: 'Atualizar senha',
    successTitle: 'Senha atualizada',
    successBody: 'Agora você já pode entrar com sua nova senha.',
    backToLoginCta: 'Ir para o login',

    // Inline error messages keyed by the `error` field the API returns
    // on a 400 from `/reset-password`.
    errors: {
      missingToken:
        'O link de redefinição está incompleto. Solicite um novo link em "Esqueci minha senha".',
      mismatch: 'As senhas não conferem.',
      tooShort: 'A senha precisa ter pelo menos {{min}} caracteres.',
      token_expired: 'Este link expirou. Solicite um novo link em "Esqueci minha senha".',
      token_invalid: 'Link inválido. Solicite um novo link em "Esqueci minha senha".',
      token_not_found: 'Link inválido. Solicite um novo link em "Esqueci minha senha".',
      invalid_password: 'Senha inválida — não atende aos requisitos de segurança.',
      password_too_short: 'A senha precisa ter pelo menos {{min}} caracteres.',
      password_too_weak:
        'Esta senha é fácil de adivinhar (parecida com senhas comuns, nomes, datas ou sequências). Use uma frase com várias palavras menos comuns.',
      password_in_breach_list:
        'Esta senha aparece em listas de vazamentos conhecidos. Escolha outra.',
      generic: 'Não foi possível atualizar a senha. Tente novamente.',
    },
    requestNewLink: 'Solicitar novo link',
  },
} as const;

export const authEn = {
  passwordRequirements: {
    minLength: 'At least {{min}} characters',
    strengthHint:
      'Avoid easy-to-guess passwords: common words, names, dates and sequences like 123. A phrase of several words is safer.',
  },

  shared: {
    backToLogin: 'Back to login',
    submit: 'Submit',
    sending: 'Sending...',
    submitAgain: 'Submit again',
    genericError: 'Something went wrong. Please try again in a moment.',
    networkError: 'Could not reach the server. Check your connection.',
  },

  forgotPassword: {
    title: 'Forgot your password',
    subtitle:
      'Enter the email tied to your account. If an account exists we will email ' +
      'you a reset link within a few minutes.',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    submit: 'Send reset link',
    successTitle: 'Check your inbox',
    successBody:
      'If an account exists for this email, you will receive a password reset ' +
      'link within a few minutes. The link expires in 1 hour.',
    successHint: "Didn't get it? Check spam or try again in a few minutes.",
  },

  devSignIn: {
    title: 'Development sign-in',
    subtitle: 'Signing in as {{email}}',
    usage: 'Open /dev/sign-in?as=<email of a local database user>&next=<path>',
    signingIn: 'Signing in…',
    errors: {
      missingEmail: 'Add the email in the ?as= parameter, for example ?as=cfo@large.local.',
      notEnabled: 'Development sign-in is off. Set DEV_SIGN_IN_ENABLED=true on the API.',
      forbidden:
        'Development sign-in refused. Use localhost and the email of an active user of the local database.',
      invalidCredentials: 'WorkOS rejected this user. Run pnpm dev:auth:sync and try again.',
      generic: 'Could not sign in. Check the API log.',
    },
  },

  magicLink: {
    title: 'Sign in with a magic link',
    subtitle:
      'Sign in without a password. We will email you a one-time code; paste it ' +
      'below to access your account.',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    requestSubmit: 'Send code',
    codeStepTitle: 'Check your inbox',
    codeStepSubtitle:
      'We sent a 6-digit code to {{email}}. Paste it below. The code expires in 10 minutes.',
    codeLabel: 'Code',
    codePlaceholder: '000000',
    submit: 'Sign in',
    resend: 'Resend code',
    changeEmail: 'Use a different email',
    backToLogin: 'Back',
    errors: {
      invalid_credentials: 'Invalid code. Make sure you typed all 6 digits correctly.',
      invalid_one_time_code: 'Invalid code. Make sure you typed all 6 digits correctly.',
      code_expired: 'This code has expired. Request a new one.',
      generic: 'Could not sign in. Please try again.',
    },
  },

  verifyEmail: {
    title: 'Verify your email',
    subtitle:
      'To finish signing in, we sent a verification code to {{email}}. Paste the ' + 'code below.',
    subtitleStandalone: 'Enter your account email and we will send you a new verification code.',
    subtitleStandaloneSent:
      'We sent a verification code to {{email}}. To finish signing in, sign in again — the code will be requested next.',
    emailLabel: 'Your account email',
    emailPlaceholder: 'you@company.com',
    codeLabel: 'Verification code',
    codePlaceholder: '000000',
    submit: 'Verify and sign in',
    sendCode: 'Send new code',
    resend: 'Did not get it? Resend the code',
    resendSuccess: 'Code resent. Check your inbox.',
    backToLogin: 'Back to login',
    errors: {
      missingToken: 'Verification session not found. Sign in again to receive a new code.',
      invalid_credentials: 'Invalid code. Make sure you typed all 6 digits correctly.',
      invalid_one_time_code: 'Invalid code. Make sure you typed all 6 digits correctly.',
      code_expired: 'This code has expired. Request a new one.',
      generic: 'Could not verify the email. Please try again.',
    },
  },

  callback: {
    loading: 'Finishing sign-in...',
    error: 'Could not complete sign-in. Please try signing in again in a moment.',
    backToLogin: 'Back to login',
    sessionConflict:
      'You are signed in as {{sessionEmail}}. Sign out to complete login as {{targetEmail}}.',
    signOutToContinue: 'Sign out and continue',
  },

  protected: {
    checking: 'Checking authentication...',
  },

  login: {
    brand: 'Alchemy',
    title: 'Sign in to your account',
    subtitle: 'The first and last ERP you need.',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Your password',
    emailInvalid: 'Enter a valid email, such as you@company.com.',
    passwordRequired: 'Enter your password to sign in.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    switchToLight: 'Switch to light mode',
    switchToDark: 'Switch to dark mode',
    welcomeBack: 'Welcome back, {{name}}.',
    googleShort: 'Google',
    magicLinkShort: 'Email link',
    devSignInHint: 'Local development — AI agent: do not type a password. Open',
    devSignInHintPath: '/dev/sign-in?as=<email of a local database user>&next=<path>',
    submit: 'Sign in',
    submitting: 'Signing in...',
    invalidCredentials: 'Incorrect email or password. Check your details and try again.',
    workosRequired:
      'This environment uses WorkOS sign-in. Provision the user in WorkOS (pnpm workos:provision) or contact your administrator.',
    magicLinkLink: 'Sign in with a magic link',
    forgotPasswordLink: 'Forgot your password',
    privacyPolicyLink: 'Privacy Policy',
    termsAndConditionsLink: 'Terms and Conditions',
    oauthDivider: 'or continue with',
    oauthOrEmailDivider: 'or',
    oauthGoogle: 'Continue with Google',
    oauthMicrosoft: 'Continue with Microsoft',
    errors: {
      generic: 'Could not sign in. Please try again in a moment.',
      emailVerificationRequired:
        'Your email is not verified yet. Complete verification to continue.',
      mfaRequired: 'Two-factor authentication is required for this account.',
      oauthDenied: 'Sign-in was cancelled at the provider. Try again.',
      oauthFailed: 'Could not complete social sign-in. Try again.',
      oauthNotProvisioned:
        'Your account is not provisioned in this environment yet. Ask an admin for an invitation or wait a moment and retry.',
      oauthNotInvited:
        'No invitation was found for this email. Ask an admin for access before using Google or Microsoft.',
      oauthEmailRequired: 'Enter your invited email before continuing with Google or Microsoft.',
      oauthStartFailed: 'Could not start social sign-in. Try again.',
    },
  },

  changePassword: {
    currentPasswordLabel: 'Current password',
    currentPasswordPlaceholder: 'Enter your current password',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'At least 6 characters',
    confirmPasswordLabel: 'Confirm new password',
    confirmPasswordPlaceholder: 'Repeat the new password',
    submit: 'Change password',
    submitting: 'Changing...',
    successAlert: 'Password changed successfully!',
    successToast: 'Password changed successfully!',
    errors: {
      currentRequired: 'Current password is required',
      newRequired: 'New password is required',
      tooShort: 'New password must be at least 6 characters',
      mismatch: 'Passwords do not match',
      sameAsCurrent: 'New password must be different from current',
      currentIncorrect: 'Current password is incorrect',
      generic: 'Could not change password. Please try again.',
    },
  },

  acceptInvitation: {
    title: 'Accept invitation',
    subtitle: 'Complete your sign-up to access your account.',
    subtitleWithEmail: 'Choose a display name and password to finish sign-up.',
    subtitleSignIn: 'You already have an account. Sign in with your password to accept.',
    subtitleSession: 'You are already signed in with this email. Confirm to accept.',
    organizationLine: 'Invitation to join {{organization}}',
    inviteeEmailLabel: 'Invitation sent to',
    loading: 'Validating invitation...',
    nameLabel: 'Full name',
    namePlaceholder: 'Your name',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    confirmLabel: 'Confirm password',
    confirmPlaceholder: '••••••••',
    currentPasswordLabel: 'Your password',
    signInHint:
      'Use the password you already sign in with. If you sign in with Google or company sign-in, sign in from the login page and open this link again.',
    forgotPasswordLink: 'Forgot your password?',
    confirmPasswordToSwitch:
      'To join the new company, confirm your password. The invitation is still valid.',
    submit: 'Accept invitation',
    submitSignIn: 'Sign in and accept',
    submitting: 'Accepting...',
    successTitle: 'Invitation accepted',
    successBody: 'All set — taking you to the dashboard.',
    goToLogin: 'Go to login',
    errors: {
      missingToken: 'The invitation link is incomplete. Ask your administrator for a new invite.',
      nameTooShort: 'Enter your full name (at least 2 characters).',
      mismatch: 'Passwords do not match.',
      tooShort: 'Password must be at least {{min}} characters.',
      passwordPolicy: 'Password must be at least {{min}} characters.',
      invitation_not_found:
        'This invitation was not found. Ask your administrator for a new invite.',
      invitation_expired: 'This invitation has expired. Ask your administrator for a new invite.',
      invitation_revoked: 'This invitation was revoked. Contact your administrator.',
      invitation_already_accepted:
        'This invitation has already been accepted. Sign in with your password instead.',
      email_already_in_use:
        'This email already has an account. Please sign in with your existing password.',
      invalid_password: 'Invalid password — it does not meet the security policy.',
      password_too_short: 'Password must be at least {{min}} characters.',
      password_too_weak:
        'This password is easy to guess (similar to common passwords, names, dates or sequences). Use a phrase of several less common words.',
      password_in_breach_list:
        'This password appears in known breach lists. Please choose another.',
      invalid_credentials:
        'Could not finish sign-up. Try again in a moment; if it keeps failing, ask your administrator for a new invite.',
      passwordRequired: 'Enter your password to continue.',
      wrongPassword:
        'Incorrect password. Try again or use "Forgot your password?" to set a new one.',
      signInElsewhere:
        'You cannot sign in from this page. Sign in from the login page and open this link again.',
      invitation_email_mismatch:
        'This invitation was sent to a different email. Sign out and sign in with the invited email.',
      sessionConflict:
        'You are signed in as {{sessionEmail}}. Sign out to accept the invitation for {{inviteeEmail}}.',
      generic: 'Could not accept the invitation. Please try again in a moment.',
    },
    signOutToContinue: 'Sign out and continue',
  },

  resetPassword: {
    title: 'Set a new password',
    subtitle: 'Pick a strong password for your account.',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: '••••••••',
    confirmPasswordLabel: 'Confirm new password',
    confirmPasswordPlaceholder: '••••••••',
    submit: 'Update password',
    successTitle: 'Password updated',
    successBody: 'You can now sign in with your new password.',
    backToLoginCta: 'Go to login',
    errors: {
      missingToken: 'The reset link is incomplete. Request a new one from "Forgot your password".',
      mismatch: 'Passwords do not match.',
      tooShort: 'Password must be at least {{min}} characters.',
      token_expired: 'This link has expired. Request a new one from "Forgot your password".',
      token_invalid: 'Invalid link. Request a new one from "Forgot your password".',
      token_not_found: 'Invalid link. Request a new one from "Forgot your password".',
      invalid_password: 'Invalid password — it does not meet the security policy.',
      password_too_short: 'Password must be at least {{min}} characters.',
      password_too_weak:
        'This password is easy to guess (similar to common passwords, names, dates or sequences). Use a phrase of several less common words.',
      password_in_breach_list:
        'This password appears in known breach lists. Please choose another.',
      generic: 'Could not update password. Please try again.',
    },
    requestNewLink: 'Request a new link',
  },
} as const;
